import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { getModelLoaderType } from '@aibuild/model-viewer';
import ZoomableImage from '@components/1-atoms/ZoomableImage';
import useAuthImage from '@hooks/useAuthImage';
import useAuthDownload from '@hooks/useAuthDownload';
import type { InlineArtifact } from '@hooks/useChat';
import { canPreviewPdf } from '@components/PdfViewer/canPreviewPdf';
import { useCanPreviewHtml } from '@components/HtmlArtifact';
import MessageBox, { MESSAGE_BOX_VARIANT_WARNING } from '@components/2-molecules/MessageBox';
import ArtifactCard from './ArtifactCard';
import {
  ArtifactFigure,
  ArtifactCaption,
  DownloadCard,
  DownloadCardInfo,
  DownloadCardName,
  DownloadCardMeta,
  StyledDownloadIcon,
  InlineImageSkeleton,
  ModerationBlockedWrapper,
} from './InlineArtifactImage.styled';

const isImageContentType = (ct: string): boolean => ct.startsWith('image/');

const MODEL_3D_CONTENT_TYPES = new Set([
  'model/gltf-binary',
  'model/gltf+json',
  'model/obj',
  'model/stl',
  'application/octet-stream+stl',
]);

const is3DModelArtifact = (artifact: InlineArtifact): boolean =>
  MODEL_3D_CONTENT_TYPES.has(artifact.contentType) || getModelLoaderType(artifact.name) !== null;

interface InlineArtifactImageProps {
  artifact: InlineArtifact;
}

const ModerationBlockedNotice: React.FC = () => (
  <ModerationBlockedWrapper>
    <MessageBox variant={MESSAGE_BOX_VARIANT_WARNING} leadingIconName="warning_0">
      <span>
        <FormattedMessage id="chat.image.unavailable" defaultMessage="This image couldn't be generated due to our content guidelines." />
      </span>
    </MessageBox>
  </ModerationBlockedWrapper>
);

const ImageArtifact: React.FC<{ artifact: InlineArtifact }> = ({ artifact }) => {
  const { url: blobUrl } = useAuthImage(artifact.moderationBlocked ? undefined : artifact.downloadUrl);


  if (artifact.moderationBlocked) return <ModerationBlockedNotice />;
  if (!blobUrl) return <InlineImageSkeleton />;

  return (
    <ArtifactFigure>
      <ZoomableImage src={blobUrl} alt={artifact.name} />
      <ArtifactCaption>{artifact.name}</ArtifactCaption>
    </ArtifactFigure>
  );
};

const FileArtifact: React.FC<{ artifact: InlineArtifact }> = ({ artifact }) => {
  const { download, downloading } = useAuthDownload();
  const handleClick = useCallback(() => {
    void download(artifact.downloadUrl, artifact.name);
  }, [download, artifact.downloadUrl, artifact.name]);

  const extension = artifact.name.split('.').pop()?.toUpperCase() ?? '';

  return (
    <DownloadCard onClick={handleClick} type="button" aria-busy={downloading}>
      <StyledDownloadIcon name="download_0" aria-hidden />
      <DownloadCardInfo>
        <DownloadCardName>{artifact.name}</DownloadCardName>
        <DownloadCardMeta>{extension} file &mdash; click to download</DownloadCardMeta>
      </DownloadCardInfo>
    </DownloadCard>
  );
};

const InlineArtifactImage: React.FC<InlineArtifactImageProps> = ({ artifact }) => {
  const canPreviewHtml = useCanPreviewHtml();
  if (isImageContentType(artifact.contentType)) {
    return <ImageArtifact artifact={artifact} />;
  }
  const fileSize = artifact.fileSize ?? 0;
  const is3DModel = is3DModelArtifact(artifact);
  const previewable = canPreviewPdf(artifact.name, fileSize, artifact.contentType)
    || canPreviewHtml(artifact.name, fileSize, artifact.contentType);
  if (is3DModel || previewable) {
    return (
      <ArtifactCard
        info={{
          artifactId: artifact.artifactId,
          name: artifact.name,
          fileSize,
          downloadUrl: artifact.downloadUrl,
          artifactType: is3DModel ? 'MODEL_3D' : undefined,
          contentType: artifact.contentType,
        }}
      />
    );
  }
  return <FileArtifact artifact={artifact} />;
};

export default InlineArtifactImage;
